import * as ko from "knockout";
import {IMessage, IMessageData, IObjectData, IRuleset} from '../../index';
import {global} from "../../appContext";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../constants";
import PromiseRequest from "../../req/PromiseRequest";
import {createErrorMessage, getObjectDefinition, IObjectDefinition, isNullOrUndefined, isPartOf} from "../../util/misc";
import {memoryStore} from "../../util/memorystore";
import {nsFa} from "../fa/fn";
import {getSearchObjectParams} from "../../util/request";
import {REQUEST_MESSAGE} from "../../constants/Language";
import i = nsFa.i;
import previewHtml from './preview.html?raw';

export let template = previewHtml;

interface IParams {
    // at least one of these should be defined
    id?: number
    message?: IMessage
}

interface IObjectType {
    objectTypeId: ObjectTypeId
    objDef: IObjectDefinition
}

export class viewModel {
    private messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private evaluationMessageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();

    private req_getmessage: PromiseRequest = global.getPromiseRequest('getmessage');
    private req_getruleset: PromiseRequest = global.getPromiseRequest('getruleset');
    private req_searchobject = global.getPromiseRequest('searchobject');

    private message: IMessage | null = null;

    private objectId!: KnockoutObservable<number | null>;
    private objectOrgId!: KnockoutObservable<string | null>;

    private objectData: KnockoutObservable<IObjectData | null> = ko.observable(null);
    private allObjectIds: KnockoutObservable<number[] | null> = ko.observable(null);

    private evalDisplayName: ExpressionEvaluator = new ExpressionEvaluator();
    private evalSubject: ExpressionEvaluator = new ExpressionEvaluator();
    private evalBody: ExpressionEvaluator = new ExpressionEvaluator();

    private availableObjectTypes: IObjectType[] = [
        ObjectTypeId.User,
        ObjectTypeId.Department,
        ObjectTypeId.Group,
        ObjectTypeId.FormData,
        ObjectTypeId.ExternalContact,
    ].map(objectTypeId => {
        const objDef = getObjectDefinition(objectTypeId);
        return {
            objectTypeId: objectTypeId,
            objDef: objDef!,
        }
    }).filter((o): o is IObjectType => o.objDef != null)
    private readonly selectedObjectTypeId: KnockoutObservable<ObjectTypeId | null> = ko.observable(null)
    private readonly selectedObjectType: KnockoutObservable<IObjectType | null> = ko.observable(null)
    private readonly showObjectTypeSelection: KnockoutObservable<boolean> = ko.observable(false)

    constructor(private params: IParams) {
        this.kosub.subscribe(global.login.selectedOrganizationId as KnockoutSubscribable<string | null>, (orgId: string | null) => {
            this.objectId(null);
            this.objectOrgId(orgId);
        });
        this.kosub.subscribe(this.objectData, (obj: IObjectData | null) => {
            const id = obj ? obj.id : null;
            this.objectId(id);
            this.fetch();
        });
        this.kosub.subscribe(this.selectedObjectTypeId, (objectTypeId: ObjectTypeId | null) => {
            const objectType = objectTypeId != null
                ? this.availableObjectTypes.find(o => o.objectTypeId == objectTypeId)
                : undefined;
            this.selectedObjectType(objectType ?? null)
            this.evalDisplayName?.reset();
            this.evalSubject?.reset();
            this.evalBody?.reset();
            this.fetchAllObjectIds()
        })
        this.init();
    }

    private init() {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});

        Promise
            .resolve()
            // fetch the message
            .then(() => {
                if (this.params.message) {
                    this.message = this.params.message;
                } else if (this.params.id) {
                    return this.req_getmessage
                        .request({id: this.params.id})
                        .then((data: IMessage[]) => {
                            this.message = data[0] ?? null;
                        });
                } else {
                    this.messageData(createErrorMessage('Missing parameter id'));
                }
            })
            // fetch ruleset (and produce object definition)
            .then(() => {
                const message = this.message;
                if (!message) {
                    return;
                }
                return Promise
                    .resolve()
                    .then(() => {
                        if (message.rulesetid) {
                            return this.req_getruleset
                                .request({id: message.rulesetid})
                                .then((data: IRuleset[]) => {
                                    return data[0]?.objecttype.id;
                                });
                        } else {
                            return null;
                        }
                    })
                    .then((objectTypeId: ObjectTypeId | null | undefined) => {
                        if (isNullOrUndefined(objectTypeId) || objectTypeId == ObjectTypeId.FormData) {
                            this.showObjectTypeSelection(true)
                            this.selectedObjectTypeId(ObjectTypeId.User)
                        } else {
                            this.showObjectTypeSelection(false)
                            this.selectedObjectTypeId(objectTypeId ?? null)
                        }
                        this.objectId = memoryStore.get('preview.object.id' + this.selectedObjectTypeId());
                        this.objectOrgId = memoryStore.get('preview.org.id' + this.selectedObjectTypeId());
                        const orgId = global.login.selectedOrganizationId();
                        if (this.objectOrgId() != orgId) {
                            this.objectId(null);
                            this.objectOrgId(orgId);
                        }
                    });
            })
            // fetch all object ids
            .then(() => {
                return this.fetchAllObjectIds()
            })
            // finished!
            .then(() => {
                this.messageData(null);
                this.isReady(true);
                if (this.objectId()) {
                    this.fetch();
                }
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
            });
    }

    private fetchAllObjectIds(): Promise<any> {
        this.allObjectIds(null)
        return this.req_searchobject
            .request(getSearchObjectParams({
                objecttypeid: this.selectedObjectTypeId() ?? ObjectTypeId.User,
                onlyids: true,
                resultsprpage: MAX_RESULTS_PER_PAGE,
            }))
            .then((data: number[]) => {
                this.allObjectIds(data);
            });
    }

    private onSelectObjectClick() {
        const selectedType = this.selectedObjectType();
        if (!selectedType) {
            return;
        }
        global.dialogManager.addComponent({
            title: `${i(selectedType.objDef.icon)} ((choose)) ((${selectedType.objDef.name}|ac))`,
            content: {
                name: 'component/object/dialog/pick',
                params: {
                    selectedObject: this.objectData,
                    objecttypeid: this.selectedObjectTypeId(),
                }
            }
        });
    }

    private onRandomObjectClick() {
        const ids = this.allObjectIds();
        if (!ids?.length) {
            return;
        }
        this.objectData(null);
        this.objectId(null);
        this.evalDisplayName.reset()
        this.evalSubject.reset()
        this.evalBody.reset()
        const randomIndex = Math.floor(Math.random() * ids.length);
        const id = ids[randomIndex];
        this.objectId(id ?? null);
        this.fetch();
    }

    private fetch() {
        const selectedType = this.selectedObjectType();
        if (!this.message || !selectedType) {
            return;
        }
        this.evaluationMessageData({message: REQUEST_MESSAGE});

        this.evalDisplayName.dispose();
        this.evalSubject.dispose();
        this.evalBody.dispose();

        Promise.resolve()
            .then(() => {
                const expression = selectedType.objDef.displayNameExpression;
                const objectId = this.objectId();
                if (objectId == null || expression == null) {
                    return;
                }
                return this.evalDisplayName.parseAndFetch(expression, objectId)
            })
            .then(() => {
                // let's not request evaluation of subject if it doesn't exist (ie. a non-email message)
                if (!this.message!.subject || !!this.showObjectTypeSelection()) {
                    return;
                }
                const objectId = this.objectId();
                if (objectId == null) {
                    return;
                }
                return this.evalSubject.parseAndFetch(this.message!.subject, objectId)
            })
            .then(() => {
                const objectId = this.objectId();
                if (objectId == null) {
                    return;
                }
                return this.evalBody.parseAndFetch(this.message!.body, objectId)
            })
            .then(() => {
                this.evaluationMessageData(null);
            })
            .catch((reason: string) => {
                this.evaluationMessageData(createErrorMessage(String(reason)));
            });
    }

    dispose() {
        this.kosub.unsuscribeAll();

        this.req_getmessage.abort();
        this.req_getruleset.abort();
        this.req_searchobject.abort();

        this.evalDisplayName.dispose();
        this.evalSubject.dispose();
        this.evalBody.dispose();
    }
}

enum EvalState {
    Requesting = 'requesting',
    Error = 'error',
    Evaluated = 'evaluated',
    Awaiting = 'awaiting',
}

class ExpressionEvaluator {
    public readonly evaluated: KnockoutObservable<string | null> = ko.observable(null);
    private readonly text: KnockoutObservable<string | null> = ko.observable(null);
    private objectId!: number;

    private req_evaluateexpression = global.getPromiseRequest('evaluateexpression')

    private getState(): EvalState {
        if (this.req_evaluateexpression.isRequesting()) {
            return EvalState.Requesting
        }
        if (this.req_evaluateexpression.isError()) {
            return EvalState.Error
        }
        if (!isNullOrUndefined(this.evaluated())) {
            return EvalState.Evaluated
        }
        return EvalState.Awaiting
    }

    private isState(...states: EvalState[]): boolean {
        return isPartOf<EvalState>(this.getState(), ...states)
    }

    parseAndFetch(expression: string | number, objectId: number): Promise<string> {
        this.text(String(expression));
        this.objectId = objectId;
        const chars = [
            '<',
            '>',
        ]

        // e.g. [DECRYPT;foo] or [DECRYPT;[foo;[bar;baz]]]
        const regExp_decrypt = /\[DECRYPT(?:\[.*?(?:\[.*?\])*?\]|.)*?\]/img
        const regExp_whiteWash = new RegExp(chars.join('|'), 'g');

        const arrReplacement: [string, string][] = []
        // cast the given expression
        let _expression: string = this.text() ?? '';

        if (!global.pv.superAdmin()) {
            _expression = _expression.replace(regExp_decrypt, (original: string) => {
                const index = arrReplacement.length + 1
                const replacement = `___replace${index}___`
                arrReplacement.push([replacement, original])
                return replacement
            })
        }
        _expression = _expression.replace(regExp_whiteWash, char => encodeURIComponent(char));
        return this.req_evaluateexpression
            .request({
                expression: _expression,
                objectid: this.objectId,
            })
            .then((data: string[]) => {
                let ret = data[0] ?? '';
                const urlencodedChars = chars.map(encodeURIComponent)
                arrReplacement.forEach(rep => ret = ret.replace(rep[0], rep[1]))
                ret = ret.replace(new RegExp(urlencodedChars.join('|'), 'g'), char => decodeURIComponent(char));
                this.evaluated(ret)
                return ret
            })
    }

    reset() {
        this.req_evaluateexpression.abort()
        this.evaluated(null)
        this.text(null)
    }

    dispose() {
        this.req_evaluateexpression.abort()
    }
}
